import {Component, OnDestroy, OnInit} from '@angular/core';
import {AuthService} from '../../../services/auth/auth.service';
import {TranslateService} from '@ngx-translate/core';
import {RouteService} from '../../../services/route/route.service';
import {SnackService} from '../../../services/snack/snack.service';
import {interval, Subscription, tap} from 'rxjs';
import moment from 'moment';
import {ViewService} from '../../../services/view/view.service';
import {ValidationService} from '../../../services/validation/validation.service';
import {UtilService} from '../../../services/util/util.service';
import {finalize} from "rxjs/operators";

@Component({
  selector: 'app-reset-password',
  templateUrl: './reset-password.component.html',
  styleUrls: ['./../login.component.scss']
})
export class ResetPasswordComponent implements OnInit, OnDestroy {
  private _subscriptions: Subscription[] = [];
  private _secret: string;
  public email: string;
  public password: string;
  public passwordRepeat: string;
  public isLoading = false;
  public isResetForm = false;
  public timeLeft: string;
  public linkExpired = false;
  public isNewUser = false;

  constructor(
    private authService: AuthService,
    private routeService: RouteService,
    private translateService: TranslateService,
    private snackService: SnackService,
    public viewService: ViewService,
    public utilService: UtilService) {}

  public ngOnInit(): void {
    if (this.routeService.isResetPassword) {
      this.initResetPasswordForm();
    } else {
      this._subscriptions.push(this.routeService.queryParams.subscribe((params) => {
        this.email = params.email || '';
      }));
    }
  }
  public ngOnDestroy(): void {
    this._subscriptions.forEach(s => s.unsubscribe());
    this._subscriptions = [];
  }

  public get passwordsMatch(): boolean {
    return this.password === this.passwordRepeat;
  }

  public get validPassword(): boolean {
    return ValidationService.validatePassword(this.password);
  }

  public get checkPassword(): boolean {
    return this.validPassword && this.passwordsMatch;
  }

  public get validEmail(): boolean {
    return this.email && ValidationService.validateEmail(this.email);
  }

  public initResetPasswordForm(): void {
    this._secret = this.routeService.params['secret'];
    this.isLoading = true;
    this.authService.checkResetLink(this._secret).pipe(
      finalize(() => this.isLoading = false),
      tap({error: () => this.routeService.navigate('/login')})
    ).subscribe((res) => {
      this.isResetForm = true;
      this.isNewUser = res.newUser;
      this.email = res.email;
      if (!this.isNewUser) {
        const validUntil: moment.Moment = moment(res.linkValidUntil);
        const timerSub = interval(1000).subscribe(() => {
          const seconds = moment().diff(validUntil, 'second') * -1;
          if (seconds > 0) {
            const format = seconds < 3600 ? 'mm:ss' : 'hh:mm:ss'; // no hour to display if under 3600
            this.timeLeft = moment().startOf('day')
              .seconds(seconds)
              .format(format);
          } else {
            this.timeLeft = this.translateService.instant('general.api_reset_password_link_expired');
            this.linkExpired = true;
          }
        });
        this._subscriptions.push(timerSub);
      }
    });
  }

  public forgot(): void {
    this.isLoading = true;
    this.authService.forgotPassword(this.email).pipe(
      finalize(() => this.isLoading = false)
    ).subscribe(() => {
      this.snackService.showSuccess(this.translateService.instant('general.api_forgot_password_succeed'));
      this.routeService.navigate('/login');
    });
  }

  public reset(): void {
    this.isLoading = true;
    this.authService.resetPassword(this.password, this._secret).pipe(
      finalize(() => this.isLoading = false)
    ).subscribe(() => {
      this.snackService.showSuccess(this.translateService.instant('general.api_reset_password_succeed'));
      this.routeService.navigate('/login');
    });
  }
}
